/**
 * Supplier Surveys Page
 * Collect supplier ESG data through surveys
 * 
 * Connected to backend API: GET /api/v1/suppliers/surveys
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../services/api_service';
import { 
  FileQuestion, 
  Send, 
  Clock, 
  CheckCircle2,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Eye,
  BarChart3,
  RefreshCw,
  ChevronRight,
  Calendar,
  Users,
  X
} from 'lucide-react';
import { API } from '../../App';

const STATUS_CONFIG = {
  draft: { label: 'Draft', bg: 'bg-[#1F1F1F]', text: 'text-[#6B6B6B]', icon: Clock },
  sent: { label: 'Sent', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Send },
  active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: Send },
  closed: { label: 'Closed', bg: 'bg-purple-500/20', text: 'text-purple-400', icon: CheckCircle2 },
  completed: { label: 'Completed', bg: 'bg-cyan-500/20', text: 'text-cyan-400', icon: CheckCircle2 },
};

const TYPE_CONFIG = {
  emissions: { label: 'Emissions', color: 'emerald' },
  due_diligence: { label: 'Due Diligence', color: 'amber' },
  labor_practices: { label: 'Labor Practices', color: 'blue' },
  water_usage: { label: 'Water Usage', color: 'cyan' },
  comprehensive: { label: 'Comprehensive', color: 'purple' },
};

const SurveyCard = ({ survey, onSend, onSelect }) => {
  const statusConfig = STATUS_CONFIG[survey.status] || STATUS_CONFIG.draft;
  const typeConfig = TYPE_CONFIG[survey.survey_type] || TYPE_CONFIG.comprehensive;
  const StatusIcon = statusConfig.icon;
  
  const deadline = survey.response_deadline ? new Date(survey.response_deadline) : null;
  const daysRemaining = deadline ? Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24)) : null;
  
  const totalSent = survey.target_supplier_ids?.length || 0;
  const responseCount = survey.response_count || 0;
  const responseRate = totalSent > 0 ? Math.round((responseCount / totalSent) * 100) : 0;

  return (
    <div 
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:border-emerald-500/30 transition-all cursor-pointer"
      data-testid={`survey-card-${survey.id}`}
      onClick={() => onSelect?.(survey)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-${typeConfig.color}-500/20 text-${typeConfig.color}-400`}>
              {typeConfig.label}
            </span>
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${statusConfig.bg} ${statusConfig.text}`}>
              <StatusIcon size={10} className="inline mr-1" />
              {statusConfig.label}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-white">{survey.survey_name}</h3>
          {deadline && (
            <div className="flex items-center gap-2 mt-1 text-xs text-[#6B6B6B]">
              <Calendar size={12} />
              Deadline: {deadline.toLocaleDateString()}
              {daysRemaining !== null && daysRemaining > 0 && (
                <span className={`px-1.5 py-0.5 rounded ${
                  daysRemaining < 7 ? 'bg-amber-500/20 text-amber-400' : 'bg-[#1F1F1F] text-[#A0A0A0]'
                }`}>
                  {daysRemaining} days left
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Response Progress */}
      <div className="mb-4 p-3 bg-[#0A0A0A] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#A0A0A0]">Response Rate</span>
          <span className="text-sm font-semibold text-white">{responseCount} / {totalSent}</span>
        </div>
        <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${
              responseRate >= 80 ? 'bg-emerald-500' :
              responseRate >= 50 ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${responseRate}%` }}
          />
        </div>
        <div className="text-right text-xs text-[#6B6B6B] mt-1">{responseRate}% complete</div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <button 
          onClick={() => onSelect?.(survey)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#1F1F1F] hover:bg-emerald-500/20 text-[#A0A0A0] hover:text-emerald-400 rounded-lg transition-colors text-sm"
        >
          <Eye size={14} />
          View Details
        </button>
        {survey.status === 'draft' && (
          <button 
            onClick={() => onSend(survey.id)}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors text-sm"
          >
            <Send size={14} />
            Send
          </button>
        )}
        {survey.status === 'sent' && (
          <button 
            onClick={() => onSelect?.(survey)}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors text-sm"
          >
            <BarChart3 size={14} />
            Results
          </button>
        )}
      </div>
    </div>
  );
};

// Create Survey Modal
const CreateSurveyModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    survey_name: '',
    survey_type: 'emissions',
    response_deadline: '',
    questions: [],
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#111111] border border-[#1F1F1F] rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create New Survey</h2>
          <button onClick={onClose} className="text-[#6B6B6B] hover:text-white">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-[#6B6B6B] mb-1 block">Survey Name</label>
            <input 
              type="text"
              value={formData.survey_name}
              onChange={(e) => setFormData({...formData, survey_name: e.target.value})}
              placeholder="e.g., 2025 Emissions Data Collection"
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="text-xs text-[#6B6B6B] mb-1 block">Survey Type</label>
            <select
              value={formData.survey_type}
              onChange={(e) => setFormData({...formData, survey_type: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-emerald-500/50 focus:outline-none"
            >
              <option value="emissions">Emissions Data</option>
              <option value="due_diligence">Due Diligence</option>
              <option value="labor_practices">Labor Practices</option>
              <option value="water_usage">Water Usage</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-[#6B6B6B] mb-1 block">Response Deadline</label>
            <input 
              type="date"
              value={formData.response_deadline}
              onChange={(e) => setFormData({...formData, response_deadline: e.target.value})}
              className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-emerald-500/50 focus:outline-none"
              required
            />
          </div>

          <div className="flex items-center gap-3 pt-4">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="flex-1 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
            >
              Create Survey
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function SupplierSurveysPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [surveys, setSurveys] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  // Fetch surveys from backend
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch surveys
        const surveysData = await apiFetch('/suppliers/surveys/list');
        if (surveysData?._error) throw new Error('Failed to fetch surveys');
        setSurveys(surveysData.items || []);

        // Fetch statistics
        try {
          const statsData = await apiFetch('/suppliers/surveys/statistics');
          if (!statsData?._error) setStatistics(statsData);
        } catch (e) {}
      } catch (err) {
        console.error('Error fetching surveys:', err);
        setError(err.message);
        // Fallback mock data
        setSurveys([
          {
            id: '1',
            survey_name: 'Annual ESG Assessment 2025',
            survey_type: 'comprehensive',
            target_supplier_ids: Array(45).fill(''),
            response_count: 38,
            status: 'sent',
            response_deadline: '2025-02-28',
          },
          {
            id: '2',
            survey_name: 'CSDDD Due Diligence Questionnaire',
            survey_type: 'due_diligence',
            target_supplier_ids: Array(30).fill(''),
            response_count: 25,
            status: 'sent',
            response_deadline: '2025-03-15',
          },
          {
            id: '3',
            survey_name: 'Carbon Footprint Survey Q4',
            survey_type: 'emissions',
            target_supplier_ids: Array(50).fill(''),
            response_count: 50,
            status: 'closed',
            response_deadline: '2025-01-15',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = survey.survey_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || survey.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleSendSurvey = async (surveyId) => {
    try {
      const data = await apiFetch(`/suppliers/surveys/${surveyId}/send`, { method: 'POST' });
      if (!data?._error) {
        setSurveys(surveys.map(s => s.id === surveyId ? {...s, status: 'sent'} : s));
      }
    } catch (err) {
      console.error('Error sending survey:', err);
    }
  };

  const handleCreateSurvey = async (formData) => {
    try {
      const data = await apiFetch('/suppliers/surveys', {
        method: 'POST',
        body: {
          ...formData,
          target_supplier_ids: [],
          questions: [
            { id: 'q1', text: 'Default question 1', type: 'text' },
          ],
        },
      });
      if (!data?._error) {
        setSurveys([...surveys, { id: data.survey_id, ...formData, status: 'draft', target_supplier_ids: [] }]);
        setShowCreateModal(false);
      }
    } catch (err) {
      console.error('Error creating survey:', err);
    }
  };

  // Calculate stats
  const totalSurveys = surveys.length;
  const activeSurveys = surveys.filter(s => s.status === 'sent' || s.status === 'active').length;
  const totalResponses = surveys.reduce((sum, s) => sum + (s.response_count || 0), 0);
  const avgResponseRate = statistics?.avg_response_rate || 
    (surveys.length > 0 
      ? Math.round(surveys.reduce((sum, s) => {
          const total = s.target_supplier_ids?.length || 0;
          const responses = s.response_count || 0;
          return sum + (total > 0 ? (responses / total) * 100 : 0);
        }, 0) / surveys.length)
      : 0);

  return (
    <div className="space-y-6" data-testid="supplier-surveys-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Supplier Surveys</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Collect ESG data from your supply chain
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="create-survey-btn"
        >
          <Plus size={16} />
          <span className="text-sm font-medium">New Survey</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <FileQuestion size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalSurveys}</div>
              <div className="text-xs text-[#6B6B6B]">Total Surveys</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Send size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{activeSurveys}</div>
              <div className="text-xs text-[#6B6B6B]">Active</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Users size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalResponses}</div>
              <div className="text-xs text-[#6B6B6B]">Total Responses</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <BarChart3 size={18} className="text-cyan-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{avgResponseRate}%</div>
              <div className="text-xs text-[#6B6B6B]">Avg Response Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search surveys..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-emerald-400 animate-spin" />
          <span className="ml-3 text-[#A0A0A0]">Loading surveys...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <AlertTriangle size={16} />
            <span>Using cached data. API connection issue: {error}</span>
          </div>
        </div>
      )}

      {/* Surveys Grid */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredSurveys.map(survey => (
            <SurveyCard 
              key={survey.id} 
              survey={survey} 
              onSend={handleSendSurvey}
              onSelect={setSelectedSurvey}
            />
          ))}
        </div>
      )}

      {!loading && filteredSurveys.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <FileQuestion size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No surveys found</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            {searchQuery || filterStatus !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first survey to collect supplier data'}
          </p>
          {!searchQuery && filterStatus === 'all' && (
            <button 
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
            >
              <Plus size={16} />
              Create Survey
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      <CreateSurveyModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSurvey}
      />

      {/* Survey Detail Panel */}
      {selectedSurvey && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="survey-detail-panel">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedSurvey(null)} />
          <div className="relative w-full max-w-2xl bg-[#0A0A0A] border-l border-[#1F1F1F] shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1F1F1F] p-6 z-10">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                      selectedSurvey.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' :
                      selectedSurvey.status === 'draft' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-[#1F1F1F] text-[#6B6B6B]'
                    }`}>{selectedSurvey.status}</span>
                    <span className="px-2 py-0.5 text-[10px] bg-[#1F1F1F] text-[#A0A0A0] rounded capitalize">{(selectedSurvey.survey_type || '').replace('_', ' ')}</span>
                  </div>
                  <h2 className="text-xl font-bold text-white">{selectedSurvey.survey_name}</h2>
                </div>
                <button onClick={() => setSelectedSurvey(null)} className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors" data-testid="close-survey-panel">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Response Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-white">{selectedSurvey.target_supplier_ids?.length || 0}</div>
                  <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">Sent To</div>
                </div>
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">{selectedSurvey.response_count || 0}</div>
                  <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">Responses</div>
                </div>
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-amber-400">{selectedSurvey.response_deadline ? new Date(selectedSurvey.response_deadline).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'}) : 'N/A'}</div>
                  <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">Deadline</div>
                </div>
              </div>

              {/* Progress */}
              <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-white">Response Rate</h4>
                  <span className="text-sm font-bold text-emerald-400">
                    {Math.round((selectedSurvey.response_count || 0) / Math.max(selectedSurvey.target_supplier_ids?.length || 1, 1) * 100)}%
                  </span>
                </div>
                <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{
                    width: `${Math.round((selectedSurvey.response_count || 0) / Math.max(selectedSurvey.target_supplier_ids?.length || 1, 1) * 100)}%`
                  }} />
                </div>
              </div>

              {/* Survey Questions Preview */}
              <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                <h4 className="text-sm font-semibold text-white mb-3">Survey Sections</h4>
                <div className="space-y-2">
                  {[
                    { name: 'Environmental Management', questions: 8, completed: selectedSurvey.response_count > 0 },
                    { name: 'Social & Labor Practices', questions: 6, completed: selectedSurvey.response_count > 0 },
                    { name: 'Governance & Ethics', questions: 5, completed: selectedSurvey.response_count > 0 },
                    { name: 'GHG Emissions Data', questions: 10, completed: selectedSurvey.response_count > 0 },
                    { name: 'Supply Chain Due Diligence', questions: 7, completed: false },
                  ].map((section, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] hover:border-emerald-500/30 cursor-pointer transition-colors" data-testid={`survey-section-${i}`}>
                      <div className="flex items-center gap-3">
                        {section.completed ? (
                          <CheckCircle2 size={16} className="text-emerald-400" />
                        ) : (
                          <Clock size={16} className="text-[#6B6B6B]" />
                        )}
                        <span className="text-sm text-white">{section.name}</span>
                      </div>
                      <span className="text-xs text-[#6B6B6B]">{section.questions} questions</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
